"use client";

import React, {useRef, useState} from "react";
import {Box, Stack, Typography} from "@mui/material";
import type {VideoData} from "@/types/dto/common/video";
import {WistiaPlayer} from "@wistia/wistia-player-react";
import {CircularPlayButton} from "@/components/shared/wistia-video";
import useScrollLock from "@/hooks/scroll-lock/use-scroll-lock";
import {resolveLandingPageAnchorTextColor} from "@/utils/landing-page-background";

interface WistiaVideoCardProps {
  videoData?: VideoData;
  subtitle: string;
}

const WistiaVideoCard = ({
  videoData,
  subtitle
}: WistiaVideoCardProps) => {
  const videoId = videoData?.wistiaVideoId;
  const playerRef = useRef<any>(null);

  const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

  useScrollLock(popoverOpen);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>)=> {
    e.preventDefault();
    if (playerRef.current === null) { return; }
    playerRef.current.play();
    setPopoverOpen(true);
  };

  const handleClosePopover = () => {
    if (playerRef.current === null) { return; }
    playerRef.current.pause();
    setPopoverOpen(false);
  };

  if (!videoId) {
    return null;
  }

  return (
    <Stack
      sx={{
        gap: "0.8rem",
        width: "100%",
        height: "auto"
      }}
    >
      <Box
        sx={{
          width: "100%",
          aspectRatio: "16 / 9",
          borderRadius: "2rem",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Box
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            position: "absolute",
            inset: 0,
            zIndex: 2,
            cursor: "pointer"
          }}
          onClick={handleClick}
        >
          <CircularPlayButton />
        </Box>
        <WistiaPlayer
          ref={playerRef}
          mediaId={videoId}
          bigPlayButton={false}
          controlsVisibleOnLoad={false}
          seo
          wistiaPopover
          onPopoverHide={handleClosePopover}
          style={{
            width: "100%",
            objectFit: "cover",
            objectPosition: "center",
            scale: "1.01"
          }}
        />
      </Box>
      <Typography
        component="div"
        variant="subtitle1"
        sx={{ "& a": { color: resolveLandingPageAnchorTextColor(""), textDecoration: "underline !important" } }}
        dangerouslySetInnerHTML={{ __html: subtitle }}
      />
    </Stack>
  );
};

export default WistiaVideoCard;